import React, { useEffect, useState } from "react";
import {
  addClientBin,
  deleteClientBin,
  getManagers,
  lookupSupplier,
  patchClientBin,
  recalcClient,
} from "../../api/paidClientsService";
import { ACCOUNT_TYPES, CLIENT_KINDS, PASSPORT_WARNINGS, SOURCES } from "../../utils/constants";
import { fmtBin, fmtDate } from "../../utils/format";
import { AmoLinkEditor, InlineText } from "../common";

// Список менеджеров один на всю сессию страницы
let managersCache = null;

function useManagers() {
  const [managers, setManagers] = useState(managersCache || []);
  useEffect(() => {
    if (managersCache) return;
    let ignore = false;
    getManagers()
      .then((list) => {
        managersCache = list;
        if (!ignore) setManagers(list);
      })
      .catch((e) => console.error(e));
    return () => {
      ignore = true;
    };
  }, []);
  return managers;
}

function AddBinForm({ onAdd, onCancel }) {
  const [bin, setBin] = useState("");
  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [lookup, setLookup] = useState(null);
  const valid = /^\d{12}$/.test(bin);

  // Проверка по реестру поставщиков, когда введено 12 цифр
  useEffect(() => {
    if (!valid) {
      setLookup(null);
      return;
    }
    let ignore = false;
    setLookup({ loading: true });
    lookupSupplier(bin)
      .then((s) => !ignore && setLookup({ supplier: s }))
      .catch((e) => {
        if (ignore) return;
        if (e.status === 404) setLookup({ text: "Не найден в реестре поставщиков — проверьте номер" });
        else setLookup({ text: e.message });
      });
    return () => {
      ignore = true;
    };
  }, [bin, valid]);

  const submit = async () => {
    if (!valid) return;
    setSaving(true);
    try {
      await onAdd(bin, confirmed);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ marginTop: 10 }}>
      <div className="pc-row">
        <input
          type="text"
          inputMode="numeric"
          className="pc-input mono browser-default"
          placeholder="12 цифр БИН / ИИН"
          maxLength={12}
          value={bin}
          autoFocus
          onChange={(e) => setBin(e.target.value.replace(/\D/g, ""))}
          onKeyDown={(e) => {
            if (e.key === "Enter") submit();
          }}
        />
        <label className="pc-row small" style={{ gap: 6, cursor: "pointer" }}>
          <input type="checkbox" checked={confirmed} onChange={(e) => setConfirmed(e.target.checked)} />
          Клиент подтвердил
        </label>
        <button type="button" className="pc-btn sm primary" disabled={!valid || saving} onClick={submit}>
          {saving ? "Добавляю…" : "Добавить"}
        </button>
        <button type="button" className="pc-btn sm" onClick={onCancel}>
          Отмена
        </button>
      </div>
      {lookup && (
        <div className="small" style={{ marginTop: 6 }}>
          {lookup.loading ? (
            <span className="muted">Ищу в реестре…</span>
          ) : lookup.supplier ? (
            <span>
              <b>{lookup.supplier.name || "Без наименования"}</b>
              {lookup.supplier.region && <span className="muted"> · {lookup.supplier.region}</span>}
            </span>
          ) : (
            <span style={{ color: "var(--warn)" }}>{lookup.text}</span>
          )}
        </div>
      )}
    </div>
  );
}

const PassportTab = ({
  userId,
  detail,
  patch,
  notify,
  reloadDetail,
  onChanged,
  onOpenClient,
  openBinDossier,
  analysingBin,
}) => {
  const [adding, setAdding] = useState(false);
  const [suggestRecalc, setSuggestRecalc] = useState(false);
  const [recalcing, setRecalcing] = useState(false);
  const managers = useManagers();

  const p = detail.passport || {};
  const bins = p.bins || [];
  const warnings = p.warnings || [];

  const afterBinsChange = () => {
    reloadDetail();
    onChanged();
  };

  const handleAddBin = async (bin, confirmed) => {
    try {
      await addClientBin(userId, bin, confirmed);
      setAdding(false);
      setSuggestRecalc(true);
      notify("БИН добавлен");
      afterBinsChange();
    } catch (e) {
      notify(e.message);
    }
  };

  const handleRecalc = async () => {
    setRecalcing(true);
    try {
      await recalcClient(userId);
      setSuggestRecalc(false);
      notify("Клиент пересчитан");
      afterBinsChange();
    } catch (e) {
      notify(e.message);
    } finally {
      setRecalcing(false);
    }
  };

  const handleConfirm = async (bin, confirmed) => {
    try {
      await patchClientBin(userId, bin, confirmed);
      afterBinsChange();
    } catch (e) {
      notify(e.message);
    }
  };

  const handleDelete = async (bin) => {
    if (!window.confirm(`Удалить БИН ${fmtBin(bin)} у клиента?`)) return;
    try {
      await deleteClientBin(userId, bin);
      notify("БИН удалён");
      afterBinsChange();
    } catch (e) {
      notify(e.message);
    }
  };

  return (
    <>
      <div className="pc-card">
        <h3>Чья это учётка</h3>
        <div className="pc-seg">
          {Object.entries(ACCOUNT_TYPES).map(([k, label]) => (
            <button
              key={k}
              type="button"
              className={detail.accountType === k ? "on" : ""}
              onClick={() =>
                detail.accountType !== k && patch({ accountType: k }, `Тип учётки сохранён: ${label}`)
              }
            >
              {label}
            </button>
          ))}
        </div>
        <p className="sub" style={{ margin: "8px 0 0" }}>
          Наших (тестировщики, сотрудники) список и сводка по умолчанию не считают.
          {detail.accountTypeSetAt && ` Изменено ${fmtDate(detail.accountTypeSetAt)}.`}
        </p>
      </div>

      <div className="pc-card">
        <h3>С каких БИН / ИИН подаётся</h3>
        {warnings.map((w) => (
          <div className="alert" key={w}>
            {PASSPORT_WARNINGS[w] || w}
          </div>
        ))}
        {suggestRecalc && (
          <div className="alert info pc-row" style={{ justifyContent: "space-between" }}>
            <span>Заявки по новому БИН появятся только после ночного расчёта.</span>
            <button type="button" className="pc-btn sm" onClick={handleRecalc} disabled={recalcing}>
              {recalcing ? "Пересчитываю…" : "🔄 Пересчитать клиента"}
            </button>
          </div>
        )}
        {!!bins.length && (
          <table className="pc-table">
            <thead>
              <tr>
                <th>БИН / ИИН</th>
                <th>Наименование и регион</th>
                <th>Подтверждён</th>
                <th>Досье БИН</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {bins.map((b) => (
                <tr key={b.bin}>
                  <td className="mono">{fmtBin(b.bin)}</td>
                  <td>
                    <div>{b.name || <span className="muted">Не найден в реестре</span>}</div>
                    {b.region && <div className="muted small">{b.region}</div>}
                  </td>
                  <td>
                    <input
                      type="checkbox"
                      checked={!!b.confirmed}
                      onChange={(e) => handleConfirm(b.bin, e.target.checked)}
                    />
                  </td>
                  <td>
                    <button
                      type="button"
                      className="pc-btn sm wrap"
                      disabled={analysingBin === fmtBin(b.bin)}
                      onClick={() => openBinDossier(fmtBin(b.bin))}
                    >
                      {analysingBin === fmtBin(b.bin) ? "Открываю…" : "Подробный анализ БИН ↗"}
                    </button>
                  </td>
                  <td>
                    <button
                      type="button"
                      className="pc-btn sm danger"
                      title="Удалить БИН"
                      onClick={() => handleDelete(b.bin)}
                    >
                      🗑
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {adding ? (
          <AddBinForm onAdd={handleAddBin} onCancel={() => setAdding(false)} />
        ) : (
          <button
            type="button"
            className="pc-btn sm"
            style={{ marginTop: 10 }}
            onClick={() => setAdding(true)}
          >
            + Добавить БИН
          </button>
        )}
      </div>

      <div className="pc-card">
        <h3>О клиенте</h3>
        <div className="pc-kv">
          <div className="k">AmoCRM</div>
          <AmoLinkEditor
            url={p.amoCrmUrl}
            onSave={(amoCrmUrl) => patch({ amoCrmUrl }, "Ссылка AmoCRM сохранена")}
          />

          <div className="k">Тип клиента</div>
          <select
            className="pc-select"
            value={p.clientKind || ""}
            onChange={(e) => patch({ clientKind: e.target.value || null })}
          >
            <option value="">Не указан</option>
            {Object.entries(CLIENT_KINDS).map(([k, l]) => (
              <option key={k} value={k}>
                {l}
              </option>
            ))}
          </select>

          <div className="k">Откуда пришёл</div>
          <select
            className="pc-select"
            value={p.source || ""}
            onChange={(e) => patch({ source: e.target.value || null })}
          >
            <option value="">Не указано</option>
            {Object.entries(SOURCES).map(([k, l]) => (
              <option key={k} value={k}>
                {l}
              </option>
            ))}
          </select>

          <div className="k">Ниша со слов клиента</div>
          <InlineText
            value={p.declaredNiche}
            placeholder="Например: Запчасти КАМАЗ"
            maxLength={200}
            onSave={(declaredNiche) => patch({ declaredNiche }, "Ниша со слов сохранена")}
          />

          <div className="k">Ответственный менеджер</div>
          <select
            className="pc-select"
            value={p.managerId || ""}
            onChange={(e) => {
              const id = e.target.value || null;
              const m = managers.find((x) => x.id === id);
              patch({ managerId: id }, "Менеджер сохранён", {
                managerName: m ? m.name || m.phone : null,
              });
            }}
          >
            <option value="">Не назначен</option>
            {p.managerId && !managers.some((m) => m.id === p.managerId) && (
              <option value={p.managerId}>{p.managerName || p.managerId}</option>
            )}
            {managers.map((m) => (
              <option key={m.id} value={m.id}>
                {m.name || m.phone}
              </option>
            ))}
          </select>

          <div className="k">Дата регистрации</div>
          <span>{fmtDate(p.registeredAt)}</span>

          <div className="k">Связанные учётки</div>
          <div className="pc-row">
            {p.linkedAccounts?.length ? (
              p.linkedAccounts.map((a) => (
                <button
                  key={a.userId}
                  type="button"
                  className="pc-btn sm"
                  title={[a.displayName, ...(a.bins || [])].filter(Boolean).join(" · ")}
                  onClick={() => onOpenClient(a.userId)}
                >
                  {a.phone || a.displayName || a.userId}
                </button>
              ))
            ) : (
              <span className="muted">нет</span>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default PassportTab;
